import { Card, Stack, Text, Badge, Group, Skeleton, Table } from '@mantine/core'
import { IconCircleCheck } from '@tabler/icons-react'
import { useZabbixMetrics } from '@/hooks/useZabbixMetrics'
import type { ZabbixProblem } from '@/hooks/useZabbixMetrics'
import { SEVERITY_COLORS, SEVERITY_ORDER } from '@/pages/dashboard/zabbixConstants'

interface ZabbixProblemsPanelProps {
  tenantId: string
}

function elapsedTime(startedAt: string): string {
  const diff = Date.now() - new Date(startedAt).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}min`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

function sortBySeverity(a: ZabbixProblem, b: ZabbixProblem): number {
  const aOrder = SEVERITY_ORDER[a.severity] ?? 99
  const bOrder = SEVERITY_ORDER[b.severity] ?? 99
  return aOrder - bOrder
}

export function ZabbixProblemsPanel({ tenantId }: ZabbixProblemsPanelProps) {
  const { data, isLoading } = useZabbixMetrics(tenantId)

  if (isLoading) {
    return (
      <Card withBorder shadow="sm" radius="md" p="lg">
        <Stack gap="md">
          <Skeleton height={20} width="40%" />
          <Skeleton height={80} />
        </Stack>
      </Card>
    )
  }

  if (!data) return null

  const problems = [...(data.problems ?? [])].sort(sortBySeverity)

  if (problems.length === 0) {
    return (
      <Card withBorder shadow="sm" radius="md" p="lg">
        <Stack align="center" gap="sm" py="md">
          <IconCircleCheck size={40} color="var(--mantine-color-green-6)" />
          <Text c="dimmed" size="sm">Nenhum problema ativo</Text>
        </Stack>
      </Card>
    )
  }

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Stack gap="md">
        <Text fw={600} size="sm">Problemas Ativos ({problems.length})</Text>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Severidade</Table.Th>
              <Table.Th>Host</Table.Th>
              <Table.Th>Problema</Table.Th>
              <Table.Th>Duração</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {problems.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <Badge color={SEVERITY_COLORS[p.severity] ?? 'gray'} size="sm">
                    {p.severity}
                  </Badge>
                </Table.Td>
                <Table.Td><Text size="sm" fw={500}>{p.host}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.name}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{elapsedTime(p.started_at)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  )
}
